import { ComposeStepState, IComposeStep } from ".";
import { is } from "immutable";
import * as React from "react";
import * as BS from "react-bootstrap";
import { Link } from "react-router-dom";
import { ContinueButtons } from "./ContinueButtons";
import { ComposeStepProps } from "./IComposeStep";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { Paths } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { AbstractSummary, SpeakerGuidelines } from "../../components";

type OwnProps = ComposeStepProps & {
};

type OwnState = ComposeStepState & {
    validationErrors: M.Validation.IFieldValidationError[];
};

export class ReviewForm extends ComponentWithValidatedForm<OwnProps, OwnState> implements IComposeStep {

    private _isMounted = false;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.Abstract.ComposeReview();
        this.state = {
            ...this.getInitialState(formModel),
            isSubmitLater: false,
            validationErrors: [],
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        if (this.props.abstract)
            this.setFormData(this.props.abstract);

        this.initialChecks();
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
        this.initialChecks(prevProps);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public setFormData(abstract: M.Abstract.Abstract) {
        // Not used
    }

    public render() {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeReview;
        const speakerGuidelinesErrors = this.getFieldErrors("agreedToSpeakerGuidelines");
        const abstractGuid = this.props.abstract ? this.props.abstract.guid : "";

        const summary = this.props.abstract && this.props.type
            ? <AbstractSummary abstract={this.props.abstract} type={this.props.type} allowEdits allowUploads />
            : null;

        const validationAlert = this.state.validationErrors.length
            ? <BS.Alert bsStyle="warning">
                <h4><Comp.Generic.Icon icon="warning" /> The following information is missing and is required for a complete abstract.</h4>
                <ul>
                    {this.state.validationErrors.map((e, i) => <li key={i}>
                        {e.errors[0]}{" "}
                        (<Link to={{
                            pathname: Paths.AbstractsCompose(this.props.eventId, e.inputName, abstractGuid),
                            state: e.inputName,
                        }}>Change</Link>)
                    </li>)}
                </ul>
            </BS.Alert>
            : null;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            <h1>Review</h1>

            {validationAlert}

            {summary}

            <hr />

            <h2>Permissions</h2>

            <Comp.Editors.BooleanField
                formComponent={this}
                name="agreedToUsagePermission"
                description={<p>
                    I give permission for my presentation to be included in a multimedia product.
                    <br />
                    <em>
                        Please note that all slides included in such a multimedia product, will be
                        embedded in the program, making them unable to be altered.
                    </em>
                </p>}
                trueLabel="I agree"
                falseLabel="I disagree"
            />

            <Comp.Editors.BooleanField
                formComponent={this}
                name="agreedToRepurposingContent"
                description={<p>
                    I am interested in discussing the possibility of converting my presentation into an Innovative Surgical Video
                    or Spotlight Case to be shared with the membership for educational purposes.
                </p>}
                trueLabel="I agree"
                falseLabel="I disagree"
            />

            <Comp.Editors.FormField name="" label="Speaker attestation">
                <p>
                    By agreeing to participate as a speaker at ASRS’ Annual meeting, I am attesting that:
                </p>

                <div className="agreement">
                    <SpeakerGuidelines />
                </div>

                <div className={speakerGuidelinesErrors.length ? "field-with-errors" : ""}>
                    <p>
                        <label>
                            <input
                                checked={formModel.data.agreedToSpeakerGuidelines || false}
                                name="agreedToSpeakerGuidelines"
                                type="checkbox"
                                onChange={() => {}}
                            /> I agree to abide by the Speaker Guidelines as detailed above.
                        </label>
                    </p>
                </div>

                <p>
                    In submitting this abstract I give permission for my abstract to be published in the Meeting Program Book
                    and other society publications as well as online.
                </p>

                {speakerGuidelinesErrors.length
                    ? speakerGuidelinesErrors.map((err: string, i) => <div className="field-with-errors" key={i}>{err}</div>)
                    : null}
            </Comp.Editors.FormField>

            <ContinueButtons
                disableSubmit={this.state.validationErrors.length > 0}
                hideSkip
                onBack={() => this.props.onBack()}
                onSave={(isSubmitLater) => this.onSaveHandler(isSubmitLater)}
                onSkip={() => this.props.onSkip()}
                processing={this.state.formProcessing}
                saveLabel="Submit your abstract"
            />

        </Comp.Validation.ValidatedForm>;
    }

    public initialChecks(prevProps?: Readonly<OwnProps>) {
        if (prevProps) {
            if ((!is(prevProps.type, this.props.type) || !is(prevProps.abstract, this.props.abstract)) && this.props.type && this.props.abstract) {
                // both abstract and type are available, so validate abstract and set form data
                const abstract = this.props.abstract;
                this.validateAbstract(this.props.type, this.props.abstract, () => {
                    this.internalSetFormData(abstract);
                });
            }
        }
        else {
            if (this.props.type && this.props.abstract) {
                // both abstract and type are available, so validate abstract and set form data
                const abstract = this.props.abstract;
                this.validateAbstract(this.props.type, this.props.abstract, () => {
                    this.internalSetFormData(abstract);
                });
            }
        }
    }

    private internalSetFormData(abstract: M.Abstract.Abstract) {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeReview;
        formModel.data.agreedToRepurposingContent = abstract.agreedToRepurposingContent;
        formModel.data.agreedToSpeakerGuidelines = abstract.agreedToSpeakerGuidelines;
        formModel.data.agreedToUsagePermission = abstract.agreedToUsagePermission;

        this.setState({
            ...this.state,
            formModel,
        });
    }

    private validateAbstract(type: M.Abstract.AbstractType, abstract: M.Abstract.Abstract, callback?: () => void) {
        const validationErrors = M.Abstract.validateAbstractForSubmission(abstract, type);
        this.setState({ ...this.state, validationErrors }, callback);
    }

    private onSaveHandler(isSubmitLater: boolean) {
        this.setState({ ...this.state, isSubmitLater }, () => {
            this.triggerSubmit();
        });
    }

    private handleSubmit(model: Forms.Abstract.ComposeReviewData) {
        const body = {
            agreedToRepurposingContent: model.agreedToRepurposingContent,
            agreedToSpeakerGuidelines: model.agreedToSpeakerGuidelines,
            agreedToUsagePermission: model.agreedToUsagePermission,
            guid: this.props.abstract ? this.props.abstract.guid : undefined,
            isSaveLater: this.state.isSubmitLater,
        };

        this.saveAbstract(model, body);
    }

    private async saveAbstract(model: Forms.Abstract.ComposeReviewData, body: any) {
        this.toggleProcessing(true);

        const result = await this.props.api.post(Api.endpoints.abstract.composeReview, {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        this.toggleProcessing(false);

        if (result.isSuccess && result.data) {
            this.props.onAbstractSaved(result.data, this.state.isSubmitLater);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
            this.toggleProcessing(false);
        }
    }
}



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/components/ReviewForm.tsx